import React, { useCallback, useEffect, useMemo, useState } from "react";
import { importacionOperacionesApi as api } from "../importacion-operaciones/importacionOperacionesApi";
import "./motor-de-pago.css";

// Los importes que se muestran son BRUTOS. PaymentAmount todavia no existe, asi que en
// ningun lado se dice "monto a pagar": decir eso seria prometer un numero que no calculamos.
const NOTA_IMPORTE = "Importe bruto · cálculo final pendiente";

// El color comunica que hacer con la fila, no que tan "linda" es.
const TONO_ELEGIBILIDAD = {
  PAYABLE_TODAY: "verde",
  ALREADY_PAID: "verde",
  FUTURE_PAYMENT: "azul",
  REVIEW_REQUIRED: "naranja",
  UNKNOWN_COMMERCIAL_RULE: "naranja",
  // Naranja, no rojo: pasó el vencimiento pero nadie dijo que siga impaga.
  DUE_DATE_PASSED_PAYMENT_UNKNOWN: "naranja",
  MISSING_FISERV: "rojo",
  DATA_INTEGRITY_ERROR: "rojo",
  // Rojo sólo con evidencia real de que sigue pendiente.
  OVERDUE: "rojo",
};

const ETIQUETA_ELEGIBILIDAD = {
  PAYABLE_TODAY: "Pagar hoy",
  FUTURE_PAYMENT: "Pago futuro",
  ALREADY_PAID: "Ya pagada",
  OVERDUE: "Vencida",
  DUE_DATE_PASSED_PAYMENT_UNKNOWN: "Vencimiento pasado · pago no verificable",
  REVIEW_REQUIRED: "Revisar",
  MISSING_FISERV: "Sin Fiserv",
  UNKNOWN_COMMERCIAL_RULE: "Sin regla comercial",
  DATA_INTEGRITY_ERROR: "Error de integridad",
};

const ETIQUETA_REPRESENTACION = {
  CURRENT_INCLUSIVE: "actual",
  LEGACY: "legacy",
  UNKNOWN: "sin clasificar",
};

// Motivo por el que una fila no tiene neto. Se muestra en la tabla en vez de un guion:
// un vacio sin explicacion se lee como error nuestro.
export const MOTIVO_SIN_CALCULO = {
  BLOCKED_UNKNOWN_COMMERCIAL_RULE: "Sin regla comercial",
  BLOCKED_REVIEW: "Identidad a revisar",
  BLOCKED_MISSING_FISERV: "Sin contraparte Fiserv",
  BLOCKED_DATA_INTEGRITY: "Error de integridad",
  NO_GOLDEN_FOR_VALIDATION: "Sin fila en producción",
  PARTIAL: "Componentes Fiserv incompletos",
};

const TABS = [
  { id: "TODAS", label: "Todas" },
  { id: "PAGAR_HOY", label: "Pagar hoy" },
  { id: "PAGO_FUTURO", label: "Pago futuro" },
  { id: "YA_PAGADAS", label: "Ya pagadas" },
  { id: "DIECIOCHO", label: "18 días" },
  { id: "CUOTAS", label: "Cuotas" },
  { id: "CALCULO_OK", label: "Cálculo OK" },
  { id: "DIFERENCIAS", label: "Diferencias" },
  { id: "REVISAR", label: "Revisar" },
  { id: "SIN_FISERV", label: "Sin Fiserv" },
  { id: "ERRORES", label: "Errores" },
];

const FILTROS = {
  TODAS: () => true,
  PAGAR_HOY: (r) => r.paymentEligibility === "PAYABLE_TODAY",
  PAGO_FUTURO: (r) => r.paymentEligibility === "FUTURE_PAYMENT",
  YA_PAGADAS: (r) => r.paymentEligibility === "ALREADY_PAID",
  // 18 dias es el plazo comercial normalizado, no el offset guardado: asi entran tanto las
  // filas de la convencion actual (17) como las legacy (18).
  DIECIOCHO: (r) => r.commercialTermBusinessDays === 18,
  CUOTAS: (r) => r.productType === "CREDIT_INSTALLMENTS",
  CALCULO_OK: (r) =>
    r.amountValidationStatus === "EXACT" || r.amountValidationStatus === "WITHIN_001",
  DIFERENCIAS: (r) =>
    r.amountValidationStatus === "WITHIN_1" || r.amountValidationStatus === "MISMATCH",
  REVISAR: (r) =>
    r.paymentEligibility === "REVIEW_REQUIRED" ||
    r.paymentEligibility === "UNKNOWN_COMMERCIAL_RULE",
  SIN_FISERV: (r) => r.paymentEligibility === "MISSING_FISERV",
  ERRORES: (r) =>
    r.paymentEligibility === "DATA_INTEGRITY_ERROR" || r.paymentEligibility === "OVERDUE",
};

export const describirEvaluacion = (datos) => {
  if (!datos?.asOf) return "";
  const fecha = formatearFecha(datos.asOf);
  return datos.asOfSource === "EXPLICIT_QUERY"
    ? `Fecha de evaluación manual: ${fecha}`
    : `Fecha de evaluación: ${fecha}`;
};

export const formatearImporte = (valor) =>
  typeof valor === "number"
    ? valor.toLocaleString("es-AR", { style: "currency", currency: "ARS", minimumFractionDigits: 2 })
    : "—";

export const formatearFecha = (valor) => {
  if (!valor) return "—";
  const fecha = new Date(valor);
  return Number.isNaN(fecha.getTime()) ? "—" : fecha.toLocaleDateString("es-AR");
};

export const describirPlazo = (fila) => {
  if (!fila?.commercialTermBusinessDays) return "—";
  const representacion = ETIQUETA_REPRESENTACION[fila.paymentTermRepresentation] || "";
  return `${fila.commercialTermBusinessDays} días hábiles${representacion ? ` (${representacion})` : ""}`;
};

function Card({ titulo, valor, importe, tono }) {
  return (
    <div className={`mdp-card mdp-card--${tono || "neutro"}`} data-testid={`mdp-card-${titulo}`}>
      <span className="mdp-card__titulo">{titulo}</span>
      <strong className="mdp-card__valor">{valor}</strong>
      {importe !== undefined && <span className="mdp-card__importe">{formatearImporte(importe)}</span>}
    </div>
  );
}

function Detalle({ fila }) {
  const f = fila.financiero || {};
  return (
    <tr className="mdp-detalle" data-testid={`mdp-detalle-${fila.fiservOperationId}`}>
      <td colSpan={13}>
        <div className="mdp-detalle__grid">
          <section>
            <h5>Identidad</h5>
            <dl>
              <dt>Clover paymentId</dt><dd>{fila.cloverPaymentId || "—"}</dd>
              <dt>Fiserv operationId</dt><dd>{fila.fiservOperationId}</dd>
              <dt>Terminal</dt><dd>{fila.terminalFiserv || "—"}</dd>
              <dt>Cupón</dt><dd>{fila.coupon || "—"}</dd>
            </dl>
          </section>
          <section>
            <h5>Conciliación</h5>
            <dl>
              <dt>matchStatus</dt><dd>{fila.matchStatus || "—"}</dd>
              <dt>rule</dt><dd>{fila.matchRule || "—"}</dd>
              <dt>financialOutcome</dt><dd>{fila.financialOutcome || "—"}</dd>
            </dl>
          </section>
          <section>
            <h5>Condición comercial</h5>
            <dl>
              <dt>Offset guardado</dt><dd>{fila.storedBusinessDayOffset ?? "—"}</dd>
              <dt>Plazo comercial</dt><dd>{describirPlazo(fila)}</dd>
              <dt>Representación</dt><dd>{fila.paymentTermRepresentation || "—"}</dd>
              <dt>Vencimiento</dt><dd>{formatearFecha(fila.merchantPaymentDueDate)}</dd>
              <dt>Pago real</dt><dd>{formatearFecha(fila.actualMerchantPaymentDate)}</dd>
            </dl>
          </section>
          <section>
            <h5>Financiero</h5>
            <dl>
              <dt>Importe bruto</dt><dd>{formatearImporte(fila.grossAmount)}</dd>
              <dt>Dto. Arancel</dt><dd>{formatearImporte(f.dtoArancel)}</dd>
              <dt>Dto. Financiero</dt><dd>{formatearImporte(f.dtoFinanciero)}</dd>
              <dt>TNA</dt><dd>{f.tna ?? "—"}</dd>
              <dt>Cuotas</dt><dd>{f.cuotas ?? "—"}</dd>
            </dl>
            <p className="mdp-nota">{NOTA_IMPORTE}</p>
          </section>
          <section className="mdp-calculo" data-testid={`mdp-calculo-${fila.fiservOperationId}`}>
            <h5>Cálculo</h5>
            <dl>
              <dt>Bruto</dt><dd>{formatearImporte(fila.grossAmount)}</dd>
              <dt>Dto. Arancel Fiserv</dt><dd>{formatearImporte(fila.merchantFeeAmount)}</dd>
              <dt>Dto. Financiero Fiserv</dt><dd>{formatearImporte(fila.financialDiscountAmount)}</dd>
              <dt>Costo anticipo Zoco</dt><dd>{formatearImporte(fila.zocoAdvanceCost)}</dd>
              <dt>Arancel comercial Zoco</dt><dd>{formatearImporte(fila.zocoCommercialFee)}</dd>
              <dt>IVA</dt><dd>{formatearImporte(fila.zocoVat)}</dd>
              <dt>Impuesto déb./créd.</dt><dd>{formatearImporte(fila.zocoDebitCreditTax)}</dd>
              <dt>Retenciones</dt><dd>{formatearImporte(fila.zocoWithholdings)}</dd>
            </dl>
            <dl className="mdp-calculo__total">
              <dt>Neto calculado</dt><dd>{formatearImporte(fila.calculatedTotalWithDiscounts)}</dd>
              <dt>Golden producción</dt><dd>{formatearImporte(fila.goldenTotalWithDiscounts)}</dd>
              <dt>Diferencia</dt><dd>{formatearImporte(fila.amountDifference)}</dd>
            </dl>
            {!fila.calculatedTotalWithDiscounts && (
              <p className="mdp-nota">{MOTIVO_SIN_CALCULO[fila.paymentAmountStatus] || fila.paymentAmountStatus}</p>
            )}
          </section>
          <section className="mdp-detalle__warnings">
            <h5>Warnings</h5>
            {fila.warnings?.length ? (
              <ul>{fila.warnings.map((w) => <li key={w}>{w}</li>)}</ul>
            ) : (
              <p className="mdp-vacio">Sin warnings</p>
            )}
          </section>
        </div>
      </td>
    </tr>
  );
}

export default function MotorDePago({ runId, token, darkMode }) {
  const [datos, setDatos] = useState(null),
    [cargando, setCargando] = useState(false),
    [error, setError] = useState(""),
    [tab, setTab] = useState("TODAS"),
    [abierta, setAbierta] = useState("");

  const cargar = useCallback(async () => {
    if (!runId) return;
    setCargando(true);
    setError("");
    try {
      setDatos(await api.obtenerPaymentEngine(runId, token));
    } catch (e) {
      setDatos(null);
      setError(e?.message || "No se pudo cargar el motor de pago.");
    } finally {
      setCargando(false);
    }
  }, [runId, token]);

  useEffect(() => {
    setDatos(null);
    setTab("TODAS");
    setAbierta("");
    cargar();
  }, [cargar]);

  const filas = useMemo(() => datos?.rows || [], [datos]);
  const visibles = useMemo(() => filas.filter(FILTROS[tab] || FILTROS.TODAS), [filas, tab]);
  const s = datos?.summary;

  if (!runId) return null;

  return (
    <section className={`mdp ${darkMode ? "mdp--dark" : ""}`} data-testid="motor-de-pago">
      <header className="mdp__header">
        <h3>Motor de pago</h3>
        {datos?.asOf && (
          <p className="mdp-evaluacion" data-testid="mdp-evaluacion">
            {describirEvaluacion(datos)}
          </p>
        )}
        <p className="mdp-nota">{NOTA_IMPORTE}</p>
        <button type="button" onClick={cargar} disabled={cargando}>
          {cargando ? "Cargando…" : "Actualizar"}
        </button>
      </header>

      {error && (
        <p className="mdp-error" role="alert" data-testid="mdp-error">
          {error}
        </p>
      )}

      {cargando && !datos && <p data-testid="mdp-cargando">Cargando motor de pago…</p>}

      {!cargando && !error && datos && s && (
        <>
          <div className="mdp-cards">
            <Card titulo="Total" valor={s.totalRows} importe={s.grossAmountTotal} />
            <Card titulo="Conciliadas" valor={s.identityResolved} importe={s.identityResolvedAmount} />
            <Card titulo="Pagar hoy" valor={s.payableToday} importe={s.payableTodayAmount} tono="verde" />
            <Card titulo="Pago futuro" valor={s.futurePayment} importe={s.futurePaymentAmount} tono="azul" />
            <Card titulo="Ya pagadas" valor={s.alreadyPaid} importe={s.alreadyPaidAmount} tono="verde" />
            <Card titulo="Revisar" valor={s.reviewRequired + s.unknownCommercialRule} importe={s.reviewRequiredAmount + s.unknownCommercialRuleAmount} tono="naranja" />
            <Card titulo="Sin Fiserv" valor={s.missingFiserv} importe={s.missingFiservAmount} tono="rojo" />
            <Card
              titulo="Vencimiento pasado"
              valor={s.dueDatePassedPaymentUnknown ?? 0}
              importe={s.dueDatePassedPaymentUnknownAmount}
              tono="naranja"
            />
            <Card titulo="Errores" valor={s.dataIntegrityError + s.overdue} importe={s.dataIntegrityErrorAmount + s.overdueAmount} tono="rojo" />
          </div>

          <div className="mdp-cards mdp-cards--calculo">
            <Card titulo="Cálculo disponible" valor={s.amountCalculatedCount ?? 0} importe={s.calculatedGrossTotal} tono="verde" />
            <Card titulo="Cálculo bloqueado" valor={(s.amountBlockedCount ?? 0) + (s.amountNoGoldenCount ?? 0)} tono="naranja" />
            <Card titulo="Neto calculado" valor={formatearImporte(s.calculatedNetTotal)} importe={s.goldenNetTotal} tono="azul" />
            <Card
              titulo="Diferencia vs producción"
              valor={formatearImporte(s.differenceTotal)}
              tono={s.mismatchCount > 0 ? "rojo" : "verde"}
            />
          </div>

          {s.duplicateAnticipoRows > 0 && (
            <p className="mdp-alerta" role="status" data-testid="mdp-duplicados">
              {s.duplicateAnticipoRows} operación(es) con más de una condición comercial cargada
              ({s.duplicateAnticipoKeys} clave(s) duplicada(s)). No se elige una: quedan como error de integridad.
            </p>
          )}

          <nav className="mdp-tabs" role="tablist">
            {TABS.map((t) => {
              const cuenta = filas.filter(FILTROS[t.id]).length;
              return (
                <button
                  key={t.id}
                  type="button"
                  role="tab"
                  aria-selected={tab === t.id}
                  className={tab === t.id ? "activo" : ""}
                  onClick={() => setTab(t.id)}
                >
                  {t.label} <span className="mdp-tabs__cuenta">{cuenta}</span>
                </button>
              );
            })}
          </nav>

          {visibles.length === 0 ? (
            <p className="mdp-vacio" data-testid="mdp-sin-resultados">
              No hay operaciones en esta vista.
            </p>
          ) : (
            <table className="mdp-tabla">
              <thead>
                <tr>
                  <th>Comercio</th>
                  <th>Fecha operación</th>
                  <th>Clover</th>
                  <th>Fiserv</th>
                  <th>Producto</th>
                  <th>Importe bruto</th>
                  <th>Neto calculado</th>
                  <th>Neto producción</th>
                  <th>Diferencia</th>
                  <th>Plazo comercial</th>
                  <th>Vencimiento</th>
                  <th>Estado pago</th>
                  <th>Warnings</th>
                </tr>
              </thead>
              <tbody>
                {visibles.map((fila) => (
                  <React.Fragment key={fila.fiservOperationId}>
                    <tr
                      className="mdp-fila"
                      onClick={() => setAbierta(abierta === fila.fiservOperationId ? "" : fila.fiservOperationId)}
                      data-testid={`mdp-fila-${fila.fiservOperationId}`}
                    >
                      <td>{fila.commerceName || fila.terminalFiserv || "—"}</td>
                      <td>{formatearFecha(fila.operationDate)}</td>
                      <td>{fila.cloverPaymentId || "—"}</td>
                      <td>{fila.fiservOperationId}</td>
                      <td>{fila.productType || "—"}</td>
                      <td>{formatearImporte(fila.grossAmount)}</td>
                      <td>
                        {fila.calculatedTotalWithDiscounts != null ? (
                          formatearImporte(fila.calculatedTotalWithDiscounts)
                        ) : (
                          <span className="mdp-bloqueo">
                            {MOTIVO_SIN_CALCULO[fila.paymentAmountStatus] || fila.paymentAmountStatus}
                          </span>
                        )}
                      </td>
                      <td>{formatearImporte(fila.goldenTotalWithDiscounts)}</td>
                      <td>{formatearImporte(fila.amountDifference)}</td>
                      <td>{describirPlazo(fila)}</td>
                      <td>{formatearFecha(fila.merchantPaymentDueDate)}</td>
                      <td>
                        <span className={`mdp-badge mdp-badge--${TONO_ELEGIBILIDAD[fila.paymentEligibility] || "neutro"}`}>
                          {ETIQUETA_ELEGIBILIDAD[fila.paymentEligibility] || fila.paymentEligibility}
                        </span>
                      </td>
                      <td>{fila.warnings?.length || 0}</td>
                    </tr>
                    {abierta === fila.fiservOperationId && <Detalle fila={fila} />}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          )}
        </>
      )}
    </section>
  );
}
